import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './AgricoProject.css';

gsap.registerPlugin(ScrollTrigger);

const A = '/content/agrico/src/assets/';

interface AgricoProjectProps {
  theme?: string;
}

const AgricoProject: React.FC<AgricoProjectProps> = ({ theme = 'light' }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [showLofi, setShowLofi] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    document.body.className = 'light';
    return () => { document.body.className = theme; };
  }, [theme]);

  useEffect(() => {
    const ctx = gsap.context(() => {

      // Mount animations (visible on load)
      gsap.from('.ap__hero img', {
        y: 70, opacity: 0, duration: 1.5, ease: 'power3.out', delay: 0.1
      });
      gsap.from('.ap__title', {
        y: 32, opacity: 0, duration: 0.9, ease: 'power3.out', delay: 0.25
      });
      gsap.from('.ap__descriptor', {
        y: 22, opacity: 0, duration: 0.8, ease: 'power3.out', delay: 0.4
      });
      gsap.from('.ap__meta-item', {
        y: 16, opacity: 0, duration: 0.65, ease: 'power3.out', stagger: 0.07, delay: 0.55
      });

      // Set initial states for scroll elements
      gsap.set('.ap-fade-up', { y: 30, opacity: 0 });
      gsap.set('.ap-fade-left', { x: -52, opacity: 0 });
      gsap.set('.ap-fade-right', { x: 52, opacity: 0 });
      gsap.set('.ap-fade-scale', { scale: 0.93, opacity: 0 });

      // Batch: generic fade up (labels, text, outcomes, process, closing)
      ScrollTrigger.batch('.ap-fade-up', {
        onEnter: (els) => gsap.to(els, {
          y: 0, opacity: 1, stagger: 0.09, duration: 0.85, ease: 'power3.out'
        }),
        start: 'top 90%',
        once: true,
      });

      // Batch: slide from left (left-side screen cards)
      ScrollTrigger.batch('.ap-fade-left', {
        onEnter: (els) => gsap.to(els, {
          x: 0, opacity: 1, stagger: 0.08, duration: 0.95, ease: 'power3.out'
        }),
        start: 'top 88%',
        once: true,
      });

      // Batch: slide from right (right-side screen cards)
      ScrollTrigger.batch('.ap-fade-right', {
        onEnter: (els) => gsap.to(els, {
          x: 0, opacity: 1, stagger: 0.08, duration: 0.95, ease: 'power3.out'
        }),
        start: 'top 88%',
        once: true,
      });

      // Batch: scale up (grid phones, dashboard)
      ScrollTrigger.batch('.ap-fade-scale', {
        onEnter: (els) => gsap.to(els, {
          scale: 1, opacity: 1, stagger: 0.08, duration: 1, ease: 'power3.out'
        }),
        start: 'top 88%',
        once: true,
      });

    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="ap" ref={rootRef}>

      {/* Hero strip */}
      <section className="ap__hero">
        <img src={`${A}8e16961190dc15513508fae8bc5450c82a3a786a.png`} alt="Agrico app on three phones" />
      </section>

      <div className="ap__container">

        {/* Project header */}
        <header className="ap__header">
          <h1 className="ap__title">Agrico</h1>
          <p className="ap__descriptor">
            A direct-to-government agricultural marketplace designed to help Indian farmers sell their produce fairly, without middlemen.
          </p>
          <div className="ap__meta">
            {[
              { label: 'Type', value: 'Product Design' },
              { label: 'Year', value: '2024' },
              { label: 'Role', value: 'Product Designer' },
              { label: 'Tools', value: 'Figma, FigJam' },
            ].map(({ label, value }) => (
              <div className="ap__meta-item" key={label}>
                <div className="ap__meta-label">{label}</div>
                <div className="ap__meta-value">{value}</div>
              </div>
            ))}
          </div>
        </header>

        {/* TL;DR + Key outcomes */}
        <section className="ap__section">
          <div className="ap__split">
            <div>
              <span className="ap__tldr-tag ap-fade-up">tl;dr</span>
              <p className="ap__body ap-fade-up" style={{ marginTop: '0.25rem' }}>
                Indian farmers lose significant income to middlemen who sit between them and government buyers. Agrico removes that layer entirely, connecting farmers directly to government procurement with live market prices, Aadhaar-based onboarding, and multilingual support built for rural India.
              </p>
            </div>
            <ul className="ap__outcomes">
              {[
                { title: 'Direct procurement', desc: 'Farmers sell straight to government buyers. No brokers, no hidden cuts.' },
                { title: 'Live market prices', desc: 'Real-time government rate cards prevent price manipulation at the point of sale.' },
                { title: 'Aadhaar onboarding', desc: 'Identity verification in under 60 seconds, designed for low-literacy users.' },
                { title: 'Multilingual from day one', desc: 'Language selection on first launch, with audio support for farmers who do not read.' },
              ].map(({ title, desc }) => (
                <li className="ap__outcome ap-fade-up" key={title}>
                  <div className="ap__outcome-dot" />
                  <div>
                    <span className="ap__outcome-strong">{title}</span>
                    <span className="ap__outcome-desc">{desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* The Problem */}
        <section className="ap__section">
          <div className="ap__section-label ap-fade-up">the problem</div>
          <div className="ap__split">
            <p className="ap__statement ap-fade-up">
              The government supports farmers with schemes and subsidies, but there is no direct channel for them to actually sell what they grow.
            </p>
            <div className="ap__body ap-fade-up">
              <p>
                Despite rising agricultural exports (India's farm exports hit USD 39 billion in 2022-23), small and marginal farmers saw little of that gain. Traditional selling channels, such as local mandis, wholesale markets, and middlemen, lack pricing transparency and consistently undercut farmers' margins.
              </p>
              <p>
                The government had the demand. Farmers had the supply. What was missing was infrastructure for them to transact directly, at fair prices, without friction.
              </p>
            </div>
          </div>
        </section>

        {/* Research */}
        <section className="ap__section">
          <div className="ap__section-label ap-fade-up">research</div>
          <div className="ap__split">
            <p className="ap__statement ap-fade-up">
              Farmers did not lack buyers. They lacked control, transparency, and trust in the system they were selling into.
            </p>
            <ul className="ap__findings">
              {[
                'Traditional channels (mandis, middlemen, wholesale) gave farmers zero pricing visibility. They had to accept whatever was offered.',
                "India's farm import growth (15.4%, USD 27.8bn) outpaced exports, with the imbalance partly driven by procurement inefficiencies.",
                "Primary research revealed that farmers' core needs were control over pricing and transparency in transactions, not just higher prices.",
                'Government procurement existed but was inaccessible to small farmers due to documentation complexity and lack of digital tools.',
              ].map((finding, i) => (
                <li className="ap__finding ap-fade-up" key={i}>{finding}</li>
              ))}
            </ul>
          </div>
          <div className="ap__news-strip ap-fade-up">
            {[
              { img: `${A}ace7284cc10332a62d129f674e5b2ebf40b80282.png`, source: 'NSO Survey' },
              { img: `${A}9a061333bd901b90f12ebbb21856cde2dc843f6b.png`, source: 'The Wire' },
            ].map(({ img, source }) => (
              <div className="ap__news-clip" key={source}>
                <img className="ap__news-img" src={img} alt={source} />
                <span className="ap__news-source">{source}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Understanding Users */}
        <section className="ap__section">
          <div className="ap__section-label ap-fade-up">understanding users</div>
          <div className="ap__split">
            <p className="ap__statement ap-fade-up">
              Two very different users. One shared problem with the same broken system.
            </p>
            <div className="ap__personas ap-fade-up">
              {[
                {
                  avatar: `${A}2b064ed64a7aa94bfea6335e455692de981500fe.png`,
                  name: 'Sandeep Kumar',
                  role: 'Small Farmer',
                  context: '34, Vidarbha region, Maharashtra. Grows soya and cotton on 2.5 acres. Class 10 education, primary Hindi literacy. Uses WhatsApp but rarely interacts with formal apps.',
                  pains: [
                    'No way to verify if the price he is offered is fair',
                    'Entirely dependent on the middleman for access to buyers',
                    'No written record of any transaction he makes',
                  ],
                },
                {
                  avatar: `${A}3ec414febfadd8c09be5cd554bfc5b43ee034734.png`,
                  name: 'Priya Mehta',
                  role: 'District Procurement Officer',
                  context: '41, State Agricultural Department. Responsible for quarterly procurement targets across 12 districts. Manages 8 field inspectors and a stack of paper forms.',
                  pains: [
                    'No real-time view of what produce is available and where',
                    'Manual verification creates days of delay per order',
                    'Fraudulent listings inflate reported supply without accountability',
                  ],
                },
              ].map(({ avatar, name, role, context, pains }) => (
                <div className="ap__persona" key={name}>
                  <img className="ap__persona-avatar" src={avatar} alt={name} />
                  <div className="ap__persona-name">{name}</div>
                  <div className="ap__persona-role">{role}</div>
                  <p className="ap__persona-detail">{context}</p>
                  <ul className="ap__persona-pains">
                    {pains.map(p => <li key={p}>{p}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Empathy map */}
          <div className="ap__empathy ap-fade-up">
            <div className="ap__empathy-label">empathy map — Sandeep Kumar</div>
            <div className="ap__empathy-grid">
              {[
                {
                  quad: 'Says',
                  items: [
                    '"I don\'t know what price I\'ll get until I\'m there"',
                    '"The broker says this is the going rate"',
                    '"I need the money quickly, I can\'t wait"',
                  ],
                },
                {
                  quad: 'Thinks',
                  items: [
                    'Am I being cheated or is this actually fair?',
                    'There is no one else I can sell to',
                    'I have no proof of what I agreed to',
                  ],
                },
                {
                  quad: 'Does',
                  items: [
                    'Accepts whatever price the broker offers',
                    'Sells early to avoid storage costs and crop loss',
                    'Relies entirely on word-of-mouth for market info',
                  ],
                },
                {
                  quad: 'Feels',
                  items: [
                    'Powerless at the point of sale',
                    'Anxious about price uncertainty before harvest',
                    'Resigned to the system as-is',
                  ],
                },
              ].map(({ quad, items }) => (
                <div className="ap__empathy-quad" key={quad}>
                  <div className="ap__empathy-quad-label">{quad}</div>
                  <ul className="ap__empathy-list">
                    {items.map(item => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* From Harvest to Payment */}
        <section className="ap__section">
          <div className="ap__section-label ap-fade-up">from harvest to payment</div>
          <div className="ap__split">
            <p className="ap__statement ap-fade-up">
              Mapping the journey exposed exactly where trust breaks down and where design could intervene.
            </p>
            <p className="ap__body ap-fade-up">
              The journey map traced Sandeep's entire selling process, from evaluating his crop to receiving payment. Each stage revealed a gap: no price data, no documented agreement, no accountability at handoff. Agrico was designed to close each one.
            </p>
          </div>
          <div className="ap__journey">
            {[
              { stage: '01  Harvest', action: 'Assess and sort', insight: 'Farmer estimates yield by eye, sorts by quality with no standardized grading.', pain: 'No benchmark to judge quality against' },
              { stage: '02  Seek', action: 'Find a buyer', insight: 'Contacts the local broker. No alternative channels, no price comparison possible.', pain: 'Zero negotiating leverage' },
              { stage: '03  Negotiate', action: 'Accept the offer', insight: 'Broker names a price. Farmer accepts because there is no reference point to push back.', pain: 'Price manipulation goes undetected' },
              { stage: '04  Transport', action: 'Move the crop', insight: 'Farmer arranges and pays for transport to the buyer location. Damage risk is his alone.', pain: 'No receipt, no proof of delivery' },
              { stage: '05  Settle', action: 'Wait for payment', insight: 'Partial cash upfront. The rest arrives days or weeks later, sometimes not at all.', pain: 'No record to dispute a shortfall' },
            ].map(({ stage, action, insight, pain }) => (
              <div className="ap__journey-step ap-fade-up" key={stage}>
                <div className="ap__journey-stage">{stage}</div>
                <div className="ap__journey-action">{action}</div>
                <p className="ap__journey-insight">{insight}</p>
                <span className="ap__journey-pain">{pain}</span>
              </div>
            ))}
          </div>
          <div className="ap__lofi-toggle-row ap-fade-up">
            <button
              className="ap__lofi-toggle"
              onClick={() => setShowLofi(v => !v)}
              aria-expanded={showLofi}
            >
              {showLofi ? 'Hide lo-fi wireframes' : 'Show lo-fi wireframes'}
              <span className="ap__lofi-chevron" style={{ transform: showLofi ? 'rotate(180deg)' : 'rotate(0deg)' }}>↓</span>
            </button>
          </div>
          {showLofi && (
            <div className="ap__artifact-wrap">
              <img
                className="ap__artifact-img"
                src={`${A}ceb50c6b9f1d135cb934019cf5cebb20d35c25cd.png`}
                alt="Low-fidelity wireframe sheet"
              />
            </div>
          )}

          {/* Design language */}
          <div className="ap__ds ap-fade-up">
            <div className="ap__ds-label">design language</div>
            <div className="ap__ds-grid">
              <div className="ap__ds-group">
                <div className="ap__ds-group-title">Palette</div>
                <div className="ap__ds-colors">
                  {[
                    { hex: '#35CB68', name: 'Leaf' },
                    { hex: '#1a1a1a', name: 'Ink' },
                    { hex: '#6e6e73', name: 'Stone' },
                    { hex: '#aeaeb2', name: 'Mist' },
                    { hex: '#f5f5f7', name: 'Cloud' },
                    { hex: '#ffffff', name: 'White', border: true },
                  ].map(({ hex, name, border }) => (
                    <div className="ap__ds-swatch" key={hex}>
                      <div className="ap__ds-swatch-dot" style={{ background: hex, border: border ? '1px solid #e5e5e5' : undefined }} />
                      <span className="ap__ds-swatch-name">{name}</span>
                      <span className="ap__ds-swatch-hex">{hex}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="ap__ds-group">
                <div className="ap__ds-group-title">Type scale</div>
                <div className="ap__ds-font-name">SF Pro<span className="ap__ds-font-sub">Display · Text · Rounded</span></div>
                <div className="ap__ds-type">
                  {[
                    { weight: 600, size: '2.25rem', label: 'Display', sample: 'Agrico' },
                    { weight: 500, size: '1.125rem', label: 'Heading', sample: 'Fair prices, live' },
                    { weight: 400, size: '0.9375rem', label: 'Body', sample: 'Farmers sell directly to government buyers.' },
                    { weight: 400, size: '0.7rem', label: 'Label', sample: 'MARKET PRICES' },
                  ].map(({ weight, size, label, sample }) => (
                    <div className="ap__ds-type-row" key={label}>
                      <span className="ap__ds-type-label">{label}</span>
                      <span className="ap__ds-type-sample" style={{ fontWeight: weight, fontSize: size }}>{sample}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* Screen grid - full bleed */}
      <section className="ap__screen-grid-section">
        <div className="ap__screen-grid">
          {[
            { img: `${A}a8271462cc01c564e440b96f2cc4d87177c428b4.png`, label: 'Language Selection' },
            { img: `${A}bb6c7138897912f9bb2926bbf76eb314a2d3bf6c.png`, label: 'Onboarding' },
            { img: `${A}8ee98804b96b97234ddd2af2d7730b5e519b46a0.png`, label: 'Home' },
            { img: `${A}ad0060b28f0ecc309c26da8fd559269a85990ccf.png`, label: 'Market Prices' },
            { img: `${A}ad3977ea482d6922bf7fbc7963788a4617f45e8f.png`, label: 'My Listings' },
          ].map(({ img, label }) => (
            <div className="ap__screen-grid-item ap-fade-scale" key={label}>
              <img className="ap__screen-grid-img" src={img} alt={label} />
              <span className="ap__screen-grid-label">{label}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="ap__container">

        {/* Key screens */}
        <section className="ap__section">
          <div className="ap__section-label ap-fade-up">key screens</div>
          <div className="ap__screens">

            <div className="ap__screen-pair">
              <div className="ap__screen-img-wrap ap-fade-left">
                <img className="ap__screen-img" src={`${A}a8271462cc01c564e440b96f2cc4d87177c428b4.png`} alt="Language selection" />
              </div>
              <div className="ap-fade-up">
                <div className="ap__screen-num">Screen 01</div>
                <div className="ap__screen-title">Language for all</div>
                <p className="ap__screen-desc">
                  The first thing Agrico asks is what language you are most comfortable in. With audio support alongside text, onboarding is accessible to farmers who do not read fluently, addressing one of the biggest barriers to digital adoption in rural India.
                </p>
              </div>
            </div>

            <div className="ap__screen-pair ap__screen-pair--flip">
              <div className="ap__screen-img-wrap ap-fade-right">
                <img className="ap__screen-img" src={`${A}8ee98804b96b97234ddd2af2d7730b5e519b46a0.png`} alt="Home screen" />
              </div>
              <div className="ap-fade-up">
                <div className="ap__screen-num">Screen 02</div>
                <div className="ap__screen-title">Everything at a glance</div>
                <p className="ap__screen-desc">
                  The home screen surfaces what matters most: inventory health score, active order statuses, and agricultural news from the government. Farmers can track their sales pipeline and upcoming dispatches without navigating multiple screens.
                </p>
              </div>
            </div>

            <div className="ap__screen-pair">
              <div className="ap__screen-img-wrap ap-fade-left">
                <img className="ap__screen-img" src={`${A}ad0060b28f0ecc309c26da8fd559269a85990ccf.png`} alt="Market prices" />
              </div>
              <div className="ap-fade-up">
                <div className="ap__screen-num">Screen 03</div>
                <div className="ap__screen-title">Fair prices, live</div>
                <p className="ap__screen-desc">
                  Live government rate cards for every crop, updated in real time. Farmers see the actual going rate before they commit to a sale, giving them the negotiating context they have never had access to before.
                </p>
              </div>
            </div>

            <div className="ap__screen-pair ap__screen-pair--flip">
              <div className="ap__screen-img-wrap ap-fade-right">
                <img className="ap__screen-img" src={`${A}ad3977ea482d6922bf7fbc7963788a4617f45e8f.png`} alt="My listings" />
              </div>
              <div className="ap-fade-up">
                <div className="ap__screen-num">Screen 04</div>
                <div className="ap__screen-title">Your listings, your record</div>
                <p className="ap__screen-desc">
                  The profile screen displays active listings, sanctioned orders, and a full transaction history with government procurers. Transparency goes both ways: farmers can see exactly where every batch of produce stands.
                </p>
              </div>
            </div>

          </div>
        </section>

      </div>

      {/* Government dashboard - full bleed */}
      <section className="ap__dashboard-section">
        <div className="ap__dashboard-label ap-fade-up">the other side</div>
        <p className="ap__dashboard-caption ap-fade-up">
          Government procurement officials get a dedicated dashboard to browse available produce by region, track orders in transit, and monitor procurement capacity at a glance.
        </p>
        <div className="ap__dashboard-img-wrap ap-fade-scale">
          <img
            className="ap__dashboard-img"
            src={`${A}9b350e4d402b76fdbe334acea10d73749aa32838.png`}
            alt="Government procurement dashboard"
          />
        </div>
      </section>

      <div className="ap__container">

        {/* Closing */}
        <div className="ap__closing">
          <p className="ap__closing-copy ap-fade-up">
            This page covers the design decisions and key outcomes. The full case study walks through primary research, empathy mapping, user flows, low-fidelity explorations, typography system, and the complete final UI.
          </p>
          <Link to="/projects/agrico/full" className="ap__full-link ap-fade-up">
            Read the full case study
          </Link>
        </div>

      </div>
    </div>
  );
};

export default AgricoProject;
